// CloneTimetableModal.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalChipSelector, ChipOption } from '../../../components/GlobalComponents/GlobalChipSelector';
import useTimetableTypes from '../../../stateManagers/hooks/useTimetableTypes';
import useClassesStore from '../../../stateManagers/store/useClassesStore';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { modalStyles } from './subparts/modalStyles';
import { useSchoolDetailsStore } from '../../../stateManagers/hooks/useSchoolDetailsStore';

interface CloneTimetableModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  sourceTimetableTypeId: string;
}

type CloneScope = 'type' | 'class';

interface MissingPeriod {
  period_number: number;
  period_label: string;
  reason: string;
  used_by_entries: number;
}

interface SkippedEntry {
  reason: string;
  period_label?: string;
  section?: string | null;
  subject_name?: string;
  teacher_name?: string;
}

const CloneTimetableModal: React.FC<CloneTimetableModalProps> = ({
  open,
  onClose,
  onSuccess,
  sourceTimetableTypeId,
}) => {
  const toast = useRef<Toast>(null);

  const timetableTypes = useTimetableTypes(true);
  const { classOptions, fetchClasses, isFetching } = useClassesStore();

  const sessionId = useSchoolDetailsStore((s) => s.schoolDetails?.current_session?.id ?? null);
  const sessionLabel = useSchoolDetailsStore((s) => s.schoolDetails?.current_session?.session ?? null);

  const [scope, setScope] = useState<CloneScope>('type');
  const [sourceTypeId, setSourceTypeId] = useState('');
  const [targetTypeId, setTargetTypeId] = useState('');
  const [sourceClassId, setSourceClassId] = useState('');
  const [sourceSection, setSourceSection] = useState<string | null>(null);
  const [targetClassId, setTargetClassId] = useState('');
  const [targetSection, setTargetSection] = useState<string | null>(null);
  const [overwrite, setOverwrite] = useState(false);

  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [skipped, setSkipped] = useState<SkippedEntry[]>([]);
  const [missingPeriods, setMissingPeriods] = useState<MissingPeriod[]>([]);

  useEffect(() => {
    if (open) {
      fetchClasses(false);
      setScope('type');
      setSourceTypeId(sourceTimetableTypeId || '');
      setTargetTypeId('');
      setSourceClassId('');
      setSourceSection(null);
      setTargetClassId('');
      setTargetSection(null);
      setOverwrite(false);
      setSkipped([]);
      setMissingPeriods([]);
    }
  }, [open, sourceTimetableTypeId, fetchClasses]);

  const typeOptions: ChipOption[] = timetableTypes
    .filter((t) => t.id && !t.label.toLowerCase().startsWith('select'))
    .map((t) => ({ value: t.id, label: t.label }));

  const classChipOptions: ChipOption[] = classOptions.map((c) => ({ value: c.id, label: c.label }));

  const sourceClass = classOptions.find((c) => c.id === sourceClassId);
  const targetClass = classOptions.find((c) => c.id === targetClassId);
  const sourceSections: string[] = sourceClass?.sections ?? [];
  const targetSections: string[] = targetClass?.sections ?? [];

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  const notify = (severity: 'success' | 'error' | 'warn', summary: string, detail: string) =>
    toast.current?.show({ severity, summary, detail, life: 6000 });

  const isSameTarget =
    sourceTypeId === targetTypeId &&
    (scope === 'type' ||
      (sourceClassId === targetClassId && (sourceSection ?? '') === (targetSection ?? '')));

  const canSubmit =
    !!sessionId &&
    !!sourceTypeId &&
    !!targetTypeId &&
    !isSameTarget &&
    (scope === 'type' ||
      (!!sourceClassId &&
        !!targetClassId &&
        (sourceSections.length === 0 || !!sourceSection) &&
        (targetSections.length === 0 || !!targetSection)));

  const handleClone = async () => {
    if (!sessionId || !sessionLabel) {
      notify('error', 'Session Error', 'No active session found. Reload the page and try again.');
      return;
    }

    setLoading(true);
    setSkipped([]);
    setMissingPeriods([]);
    try {
      const payload: Record<string, any> = {
        source_timetable_type_id: sourceTypeId,
        source_session_id: sessionId,
        target_timetable_type_id: targetTypeId,
        target_session_id: sessionId,
        target_session: sessionLabel,
        overwrite,
      };

      if (scope === 'class') {
        payload.source_class_id = sourceClassId;
        payload.source_section = sourceSection;
        payload.target_class_id = targetClassId;
        payload.target_section = targetSection;
      }

      const response = await apiClient.post('/api/timetable/clone', payload);

      if (response.data.status === 'success') {
        const cloned = response.data.cloned_count ?? 0;
        const skippedList: SkippedEntry[] = response.data.skipped ?? [];
        setSkipped(skippedList);

        if (skippedList.length > 0) {
          notify(
            'warn',
            'Cloned with Skips',
            `${cloned} ${cloned === 1 ? 'entry' : 'entries'} cloned, ${skippedList.length} skipped.`,
          );
        } else {
          notify('success', 'Cloned', `${cloned} timetable ${cloned === 1 ? 'entry' : 'entries'} cloned.`);
          setTimeout(() => {
            onSuccess();
            handleClose();
          }, 400);
          return;
        }
        onSuccess();
      } else {
        notify('error', 'Clone Failed', response.data.message || 'Could not clone timetable.');
      }
    } catch (err: any) {
      // 409 with missing_periods → the target type does not have the source's periods.
      const missing: MissingPeriod[] = err.response?.data?.missing_periods ?? [];
      if (missing.length > 0) {
        setMissingPeriods(missing);
        notify(
          'error',
          'Missing Time Slots',
          `The target timetable type is missing ${missing.length} ${missing.length === 1 ? 'period' : 'periods'} used by the source.`,
        );
      } else {
        notify('error', 'Clone Failed', err.response?.data?.message || 'Could not clone timetable.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.timetable-entry-modal .p-dialog');
      const mask = document.querySelector('.timetable-entry-modal');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '8px 0' }}>
      <div style={{
        width: '54px', height: '54px', borderRadius: '14px',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', fontSize: '24px', boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
        flexShrink: 0, border: '3px solid white',
      }}>
        <i className="pi pi-copy" />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e40af', marginBottom: '4px' }}>
          Clone Timetable
        </div>
        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
          {scope === 'type'
            ? 'Copy every class schedule from one timetable type to another'
            : 'Copy one class/section schedule onto another class/section'}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: '900px', borderRadius: '24px', overflow: 'hidden' }}
        contentStyle={{
          padding: '18px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)',
          overflowY: 'auto',
        }}
        modal
        dismissableMask
        draggable={false}
        resizable={false}
        blockScroll
        maskClassName="timetable-entry-modal"
      >
        <style>{modalStyles}</style>
        <style>{`
          .clone-scope-button {
            flex: 1;
            padding: 12px 14px;
            border-radius: 12px;
            border: 1.5px solid #e5e7eb;
            background: white;
            color: #1f2937;
            font-size: 13px;
            font-weight: 600;
            cursor: pointer;
            text-align: left;
            transition: all 0.2s ease;
          }
          .clone-scope-button.selected {
            border-color: #3b82f6;
            background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
            color: white;
            box-shadow: 0 2px 8px rgba(59,130,246,0.25);
          }
          .clone-scope-button small { display: block; font-weight: 500; opacity: 0.85; margin-top: 3px; }
          .overwrite-toggle {
            display: flex; align-items: center; gap: 10px;
            padding: 12px 14px; border-radius: 12px;
            background: rgba(239,68,68,0.06); border: 1px solid rgba(239,68,68,0.25);
            cursor: pointer;
          }
        `}</style>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* ── Scope ── */}
          <div className="field-section">
            <div className="section-header">
              <div className="icon-circle"><i className="pi pi-clone" /></div>
              <span>What to Clone</span>
            </div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button
                type="button"
                className={`clone-scope-button ${scope === 'type' ? 'selected' : ''}`}
                onClick={() => setScope('type')}
                disabled={loading}
              >
                Entire Timetable Type
                <small>All classes and sections in the source type</small>
              </button>
              <button
                type="button"
                className={`clone-scope-button ${scope === 'class' ? 'selected' : ''}`}
                onClick={() => setScope('class')}
                disabled={loading}
              >
                Single Class / Section
                <small>Copy one schedule onto another class or section</small>
              </button>
            </div>
          </div>

          {/* ── Source ── */}
          <div className="field-section">
            <div className="section-header">
              <div className="icon-circle"><i className="pi pi-download" /></div>
              <span>Source</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <GlobalChipSelector
                label="Source Timetable Type"
                icon="pi pi-calendar"
                value={sourceTypeId}
                onChange={setSourceTypeId}
                options={typeOptions}
                required
                disabled={loading}
                emptyMessage="No timetable types available"
              />

              {scope === 'class' && (
                <>
                  <GlobalChipSelector
                    label="Source Class"
                    icon="pi pi-users"
                    value={sourceClassId}
                    onChange={(v) => { setSourceClassId(v); setSourceSection(null); }}
                    options={classChipOptions}
                    required
                    loading={isFetching}
                    disabled={loading}
                    emptyMessage="No classes available"
                  />
                  {sourceSections.length > 0 && (
                    <GlobalChipSelector
                      label="Source Section"
                      icon="pi pi-list"
                      value={sourceSection || ''}
                      onChange={setSourceSection}
                      options={sourceSections.map((s) => ({ value: s, label: `Section ${s}` }))}
                      required
                      disabled={loading}
                    />
                  )}
                </>
              )}
            </div>
          </div>

          {/* ── Target ── */}
          <div className="field-section">
            <div className="section-header">
              <div className="icon-circle"><i className="pi pi-upload" /></div>
              <span>Target</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <GlobalChipSelector
                label="Target Timetable Type"
                icon="pi pi-calendar"
                value={targetTypeId}
                onChange={setTargetTypeId}
                options={typeOptions}
                required
                disabled={loading}
                emptyMessage="No timetable types available"
              />

              {scope === 'class' && (
                <>
                  <GlobalChipSelector
                    label="Target Class"
                    icon="pi pi-users"
                    value={targetClassId}
                    onChange={(v) => { setTargetClassId(v); setTargetSection(null); }}
                    options={classChipOptions}
                    required
                    loading={isFetching}
                    disabled={loading}
                    emptyMessage="No classes available"
                  />
                  {targetSections.length > 0 && (
                    <GlobalChipSelector
                      label="Target Section"
                      icon="pi pi-list"
                      value={targetSection || ''}
                      onChange={setTargetSection}
                      options={targetSections.map((s) => ({ value: s, label: `Section ${s}` }))}
                      required
                      disabled={loading}
                    />
                  )}
                </>
              )}

              <div className="info-note">
                <i className="pi pi-info-circle" />
                Each source period is re-matched against the target type's own time slots on
                <strong> period number + period label</strong>, and the cloned rows use the target's slot
                ids. If any period is missing from the target type, nothing is cloned and the gaps are
                listed here.
              </div>

              <div className="overwrite-toggle" onClick={() => !loading && setOverwrite(!overwrite)}>
                <input type="checkbox" checked={overwrite} readOnly disabled={loading} />
                <div style={{ fontSize: '12.5px', color: '#7f1d1d', fontWeight: 600 }}>
                  Replace existing target entries
                  <div style={{ fontWeight: 500, color: '#991b1b', marginTop: '2px' }}>
                    Deletes the target's current entries before cloning. Without this, the clone is
                    rejected if the target already has entries.
                  </div>
                </div>
              </div>

              {isSameTarget && sourceTypeId && targetTypeId && (
                <div className="info-note" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: '#991b1b' }}>
                  <i className="pi pi-exclamation-triangle" />
                  Source and target are identical — pick a different target.
                </div>
              )}
            </div>
          </div>

          {/* ── Missing target time slots (hard stop) ── */}
          {missingPeriods.length > 0 && (
            <div className="field-section" style={{ border: '1px solid rgba(239,68,68,0.35)' }}>
              <div className="section-header">
                <div className="icon-circle" style={{ background: 'linear-gradient(135deg, #991b1b 0%, #ef4444 100%)' }}>
                  <i className="pi pi-ban" />
                </div>
                <span style={{ color: '#991b1b' }}>Missing Time Slots ({missingPeriods.length})</span>
              </div>
              <div style={{ fontSize: '12.5px', color: '#7f1d1d', marginBottom: '10px' }}>
                Nothing was cloned. Add these periods to the target timetable type, then run the clone again.
              </div>
              <div style={{ maxHeight: '220px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {missingPeriods.map((m, i) => (
                  <div key={i} style={{
                    fontSize: '12.5px', color: '#475569', background: 'rgba(239,68,68,0.05)',
                    border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', padding: '8px 10px',
                  }}>
                    <strong style={{ color: '#991b1b' }}>
                      Period {m.period_number} — {m.period_label}
                    </strong>
                    <span style={{ color: '#64748b' }}>
                      {' '}• used by {m.used_by_entries} {m.used_by_entries === 1 ? 'entry' : 'entries'}
                    </span>
                    <div style={{ color: '#b91c1c', marginTop: '2px' }}>{m.reason}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Skipped report ── */}
          {skipped.length > 0 && (
            <div className="field-section">
              <div className="section-header">
                <div className="icon-circle" style={{ background: 'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)' }}>
                  <i className="pi pi-exclamation-triangle" />
                </div>
                <span>Skipped ({skipped.length})</span>
              </div>
              <div style={{ maxHeight: '220px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {skipped.map((s, i) => (
                  <div key={i} style={{
                    fontSize: '12.5px', color: '#475569', background: '#f8fafc',
                    border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 10px',
                  }}>
                    <strong style={{ color: '#1e40af' }}>{s.period_label || 'Period'}</strong>
                    {s.section ? ` • Section ${s.section}` : ''}
                    {s.subject_name ? ` • ${s.subject_name}` : ''}
                    {s.teacher_name ? ` • ${s.teacher_name}` : ''}
                    <div style={{ color: '#b45309', marginTop: '2px' }}>{s.reason}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ textAlign: 'center', marginTop: '10px' }}>
            <GlobalButton
              label={loading ? 'Cloning…' : 'Clone Timetable'}
              icon="pi pi-copy"
              onClick={handleClone}
              variant="primary"
              size="medium"
              disabled={!canSubmit || loading}
              loading={loading}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default CloneTimetableModal;